import type {
  AmhsCollisionFrame,
  AmhsJunctionWaitFrame,
  AmhsLiveInventory,
  AmhsLiveReplicaBatch,
  AmhsLiveReportPayload,
  AmhsStockerDropSummary,
  AmhsStreamFrame,
  AmhsStreamSnapshotFrame,
  AmhsStreamVehicleLeg,
  AmhsTransferFrame,
  OhtTimelineEvent,
} from '../../types/amhsStream';
import type { TransportRequest, VisualizationEvent } from '../../types/simulationPlayback';
import { isJunctionWaitLeg } from '../../utils/streamedJunctionWaits';
import {
  applyLiveStockerInventoryChange,
  mapStreamInventory,
  type StockerInventoryChange,
} from './applyLiveStockerInventory';
import { collectToolPickupReady } from './toolPickupReady';
import { transferAnimationPipelineStore } from '../../store/transferAnimationPipelineStore';
import { resolveTransferStationId } from './transferStationId';

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function normalizeLeg(
  raw: Record<string, unknown> | null | undefined,
  fallbackIndex: number
): AmhsStreamVehicleLeg | null {
  if (!raw || typeof raw !== 'object') return null;
  const vehicleId = String(raw.vehicleId ?? raw.ohtId ?? '').trim();
  if (!vehicleId) return null;
  const requestId = String(raw.requestId ?? raw.transportRequestId ?? '').trim();
  const startS = asNumber(raw.startS ?? raw.startSec);
  const endS = asNumber(raw.endS ?? raw.endSec, startS);
  return {
    legId: String(raw.legId ?? `${requestId}:${vehicleId}:${startS}:${fallbackIndex}`),
    vehicleId,
    ohtId: String(raw.ohtId ?? vehicleId),
    requestId,
    foupId: typeof raw.foupId === 'string' ? raw.foupId : undefined,
    startS,
    endS,
    fromId: String(raw.fromId ?? raw.fromNodeId ?? raw.fromPortId ?? ''),
    toId: String(raw.toId ?? raw.toNodeId ?? raw.toPortId ?? ''),
    fromX: asNumber(raw.fromX),
    fromY: asNumber(raw.fromY),
    toX: asNumber(raw.toX),
    toY: asNumber(raw.toY),
    phase: String(raw.phase ?? 'delivery'),
    speed: asNumber(raw.speed),
    waitS: asNumber(raw.waitS),
    segmentId: typeof raw.segmentId === 'string' ? raw.segmentId : undefined,
    pathLengthM: asNumber(raw.pathLengthM),
    fleet: typeof raw.fleet === 'string' ? raw.fleet : undefined,
    hopKind: typeof raw.hopKind === 'string' ? raw.hopKind : undefined,
    reason: typeof raw.reason === 'string' ? raw.reason : undefined,
    streamSequence:
      typeof (raw.streamSequence ?? raw.sequence) === 'number'
        ? asNumber(raw.streamSequence ?? raw.sequence)
        : undefined,
  };
}

function transportRequestId(request: TransportRequest & Record<string, unknown>): string {
  return String(request.transportRequestId ?? request.requestId ?? '').trim();
}

/**
 * The stream omits inapplicable ids by sending `""` rather than dropping the
 * key, so `??` keeps the blank and a gate transfer looks like it has a station.
 */
function optionalId(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function normalizeJunctionWait(frame: AmhsJunctionWaitFrame): AmhsJunctionWaitFrame {
  if (frame.reason !== 'nodeCapacity') return frame;
  const waitSec = asNumber(frame.waitSec, -1);
  if (waitSec < 0) return frame;

  // nodeCapacity frames describe the actual queue duration in waitSec. Some
  // streams send untilSimTimeS from a later planning horizon, which otherwise
  // pins the OHT long after the queue has cleared.
  return {
    ...frame,
    untilSimTimeS: frame.simTimeS + waitSec,
  };
}

/**
 * Frame types whose `simTimeS` is the publish tick, so they can drive the
 * playback clock.
 *
 * Lookahead frames are deliberately absent: `junction_wait` stamps the sim time
 * of a dwell the planner has scheduled (also exposed as `untilSimTimeS`) and
 * `transfer_event` stamps the handoff instant (`resumeSimTimeS`). Both run well
 * ahead of the tick — measured up to 172 s and 71 s respectively — and letting
 * them raise the clock teleported vehicles past every leg the UI had.
 */
const TICK_BEARING_FRAME_TYPES = new Set([
  'stream_started',
  'stream_snapshot',
  'stream_layout',
  'heartbeat',
  'vehicle_leg',
  'inventory',
  'stocker_inventory',
  'bay_inventory',
  'transport_request',
  'transport_request_update',
  'collision_wait',
  'collision_clear',
]);

/** Reports per diagnostic tag per stream, in development. */
const LOG_BUDGET_PER_TAG = 25;

export class AmhsLiveReplica {
  private readonly legs = new Map<string, AmhsStreamVehicleLeg>();
  private readonly reportedRouteMismatches = new Set<string>();
  private readonly reportedOrderAmbiguities = new Set<string>();
  private readonly reportedDuplicates = new Set<string>();
  private readonly logBudgets = new Map<string, number>();
  private readonly transportRequests = new Map<string, TransportRequest>();
  private readonly transferEvents = new Map<string, VisualizationEvent>();
  /**
   * Bumped on every transfer-event mutation. Prune retains a bounded window;
   * the revision lets an unchanged log reuse the previous drained array (and
   * with it the previous replay plan) instead.
   */
  private transferEventsRevision = 0;
  private drainedTransferEvents: {
    revision: number;
    events: VisualizationEvent[];
  } | null = null;
  private readonly collisions = new Map<string, AmhsCollisionFrame>();
  private readonly junctionWaits = new Map<string, AmhsJunctionWaitFrame>();
  private readonly legSequences = new Map<string, number>();
  private readonly transferFrames = new Map<
    string,
    { frame: AmhsTransferFrame; sequence: number }
  >();
  private readonly junctionWaitSequences = new Map<string, number>();
  private readonly ohtCargo = new Map<string, string>();
  private readonly lastStockerDrops = new Map<string, AmhsStockerDropSummary>();
  private simTimeS = 0;
  private inventory: AmhsLiveInventory | undefined;
  private kpis: Record<string, number> | undefined;
  private liveReport: AmhsLiveReportPayload | undefined;
  private eventCursor: number | undefined;
  private executionTimeByOht = new Map<string, number>();
  private sequence = 0;
  private ingestSequence = 0;
  private simSpeed = 1;

  /** Cap transfer / request logs so long live runs do not grow without bound. */
  private readonly maxRetainedTransferEvents = 2000;
  private readonly maxRetainedTransportRequests = 2000;
  private readonly maxRetainedTransferFrames = 1000;

  constructor(private maxRetainedLegs = 500) { }

  /**
   * Report a re-sent frame once per identity, in development only.
   *
   * A stream can repeat the same junction wait on every tick it is still
   * waiting, which arrives hundreds of times a second. Logging each one built a
   * payload object and, under the dev server, shipped it to the terminal over
   * the HMR socket — on the same thread that has to animate the fab. The first
   * occurrence carries all the diagnostic value, so the repeats are dropped
   * before the details are even assembled.
   */
  private reportDuplicateOnce(key: string, details: () => Record<string, unknown>): void {
    if (!import.meta.env.DEV || this.reportedDuplicates.has(key)) return;
    // A long run keeps producing new identities; the cap keeps this from
    // becoming its own leak, at the cost of re-reporting after a wrap.
    if (this.reportedDuplicates.size >= 5000) this.reportedDuplicates.clear();
    this.reportedDuplicates.add(key);
    if (!this.withinLogBudget('DUPLICATE_EVENT')) return;
    console.warn('[DUPLICATE_EVENT]', details());
  }

  /**
   * Total per-tag budget for a repeating diagnostic, in development only.
   *
   * Deduping by identity is not enough for diagnostics whose key includes the
   * simulation time: every occurrence is a new identity, so a long run logs
   * forever. Each `console.warn` serializes a payload and, under the dev
   * server, ships it to the terminal over the HMR socket — on the thread that
   * has to animate the fab. The first reports carry the diagnostic value, so
   * the tail is dropped with one note that it was.
   */
  private withinLogBudget(tag: string): boolean {
    const used = this.logBudgets.get(tag) ?? 0;
    if (used > LOG_BUDGET_PER_TAG) return false;
    this.logBudgets.set(tag, used + 1);
    if (used === LOG_BUDGET_PER_TAG) {
      console.warn(
        `[${tag}] reached ${LOG_BUDGET_PER_TAG} reports for this stream; suppressing the rest.`
      );
      return false;
    }
    return true;
  }

  setMaxRetainedLegs(value: number): void {
    if (Number.isInteger(value) && value > 0) this.maxRetainedLegs = value;
    this.pruneRetainedHistory();
  }

  setSimSpeed(value: number): void {
    if (Number.isFinite(value) && value > 0) this.simSpeed = value;
  }

  setExecutionTimes(times: Readonly<Record<string, number>>): void {
    this.executionTimeByOht = new Map(
      Object.entries(times).filter(([, value]) => Number.isFinite(value))
    );
  }

  applySnapshot(frame: AmhsStreamSnapshotFrame): void {
    this.legs.clear();
    this.reportedRouteMismatches.clear();
    this.reportedOrderAmbiguities.clear();
    this.transportRequests.clear();
    this.transferEvents.clear();
    this.transferEventsRevision += 1;
    this.collisions.clear();
    this.junctionWaits.clear();
    this.legSequences.clear();
    this.transferFrames.clear();
    this.junctionWaitSequences.clear();
    this.ohtCargo.clear();
    this.lastStockerDrops.clear();
    this.executionTimeByOht.clear();
    this.simTimeS = frame.snapshot.simTimeS ?? frame.simTimeS;
    this.eventCursor = frame.eventCursor;
    this.inventory = frame.snapshot.inventory;
    this.kpis = frame.snapshot.kpis;
    this.liveReport = undefined;

    (frame.snapshot.vehicleLegs ?? []).forEach((raw, index) => {
      const leg = normalizeLeg(raw as unknown as Record<string, unknown>, index);
      if (leg) {
        this.legs.set(leg.legId, leg);
        this.legSequences.set(leg.legId, this.ingestSequence++);
        if (
          leg.foupId &&
          leg.phase !== 'deadhead' &&
          leg.startS <= this.simTimeS &&
          this.simTimeS < leg.endS
        ) {
          this.ohtCargo.set(leg.vehicleId, leg.foupId);
        }
      }
    });
    (frame.snapshot.transportRequests ?? []).forEach((request) => {
      const id = transportRequestId(request);
      if (id) this.transportRequests.set(id, request);
    });
    [...(frame.snapshot.visualizationEvents ?? [])]
      .sort((a, b) => a.t - b.t || a.sequence - b.sequence)
      .forEach((event) => {
        this.transferEvents.set(event.eventId, event);
        this.transferEventsRevision += 1;
        this.sequence = Math.max(this.sequence, event.sequence + 1);
        this.applyTransferProjection(event);
      });
    this.pruneRetainedHistory();
  }

  enqueue(frame: AmhsStreamFrame): void {
    if (typeof frame.simTimeS === 'number' && TICK_BEARING_FRAME_TYPES.has(frame.type)) {
      this.simTimeS = Math.max(this.simTimeS, frame.simTimeS);
    }

    switch (frame.type) {
      case 'vehicle_leg': {
        const leg = normalizeLeg(
          frame.vehicleLeg as unknown as Record<string, unknown>,
          this.legs.size
        );
        if (leg) {
          const existing = this.legs.get(leg.legId);
          // A look-ahead stream may publish the same leg more than once. Keep
          // the first immutable schedule entry: replacing an active leg would
          // change its geometry underneath the renderer.
          if (!existing && leg.endS < this.simTimeS - 0.001) {
            if (this.withinLogBudget('LATE_EVENT')) {
              console.warn('[LATE_EVENT]', {
                type: frame.type,
                eventId: leg.legId,
                ohtId: leg.vehicleId,
                eventStartS: leg.startS,
                eventEndS: leg.endS,
                streamNowS: this.simTimeS,
                action: 'ignored_completed_motion',
              });
            }
          } else if (!existing) {
            this.legs.set(leg.legId, leg);
            this.legSequences.set(leg.legId, this.ingestSequence++);
            if (import.meta.env.DEV) {
              console.debug(
                `[${leg.vehicleId}][WS] legId=${leg.legId} simTime=${frame.simTimeS} ` +
                `start=${leg.startS} end=${leg.endS} action=QUEUED`
              );
            }
          } else if (
            existing.startS !== leg.startS ||
            existing.endS !== leg.endS ||
            existing.fromId !== leg.fromId ||
            existing.toId !== leg.toId
          ) {
            this.reportDuplicateOnce(`leg:${leg.legId}`, () => ({
              type: frame.type,
              eventId: leg.legId,
              ohtId: leg.vehicleId,
              action: 'kept_first_schedule',
              existing,
              duplicate: leg,
            }));
          }
        } else {
          // A leg frame the UI cannot read would otherwise vanish without a
          // trace, and look exactly like a stream that never sent legs at all.
          if (this.withinLogBudget('VEHICLE_NOT_FOUND')) {
            console.warn('[VEHICLE_NOT_FOUND]', {
              type: frame.type,
              simTimeS: frame.simTimeS,
              payload: frame,
            });
          }
        }
        break;
      }
      case 'transfer_event': {
        const eventId = `${frame.transportRequestId}:${frame.eventType}:${frame.simTimeS}:${frame.portId}`;
        const holdSimSec =
          typeof frame.holdSimSec === 'number' && frame.holdSimSec >= 0
            ? frame.holdSimSec
            : typeof frame.holdWallSec === 'number' && frame.holdWallSec >= 0
              ? frame.holdWallSec * this.simSpeed
              : typeof frame.resumeSimTimeS === 'number'
                ? Math.max(0, frame.resumeSimTimeS - frame.simTimeS)
                : undefined;
        const stockerId = optionalId(frame.stockerId);
        const toolId = optionalId(frame.toolId);
        // Equipment DROP events distinguish the short vehicle handover from
        // the much longer tool/process window. The HAR records, for example,
        // DROP at 1629.5, vehicle free at 1637.5, but resume at 1829.5.
        // The OHT/FOUP handoff must finish at vehicleFreeSimTimeS, not minutes
        // later at resumeSimTimeS.
        const equipmentVehicleFreeS =
          toolId &&
            frame.eventType === 'DROP' &&
            typeof frame.vehicleFreeSimTimeS === 'number' &&
            Number.isFinite(frame.vehicleFreeSimTimeS) &&
            frame.vehicleFreeSimTimeS >= frame.simTimeS
            ? frame.vehicleFreeSimTimeS
            : undefined;
        const visualEndS =
          equipmentVehicleFreeS ?? frame.simTimeS + (holdSimSec ?? 0);
        const event: VisualizationEvent = {
          sequence: this.sequence,
          eventId,
          // A request normally has multiple transfers (entry PICKUP, stocker
          // DROP, later PICKUP, equipment DROP...). Each streamed transfer is
          // one independent choreography; grouping all of them by request made
          // progress span from the first pickup to the final drop.
          operationId: eventId,
          kind: toolId
            ? 'EQUIPMENT_TRANSFER'
            : frame.eventType === 'PICKUP'
              ? 'OHT_PICKUP'
              : 'OHT_DROP',
          phase: 'handoff',
          intent: 'physical',
          action: frame.eventType,
          t: frame.simTimeS,
          ...(visualEndS > frame.simTimeS ? { endT: visualEndS } : {}),
          ...(frame.holdWallSec != null ? { holdWallSec: frame.holdWallSec } : {}),
          ...(holdSimSec != null ? { holdSimSec } : {}),
          ...(frame.resumeSimTimeS != null ? { resumeSimTimeS: frame.resumeSimTimeS } : {}),
          ...(frame.vehicleHandoverSec != null
            ? { vehicleHandoverSec: frame.vehicleHandoverSec }
            : {}),
          ...(frame.vehicleFreeSimTimeS != null
            ? { vehicleFreeSimTimeS: frame.vehicleFreeSimTimeS }
            : {}),
          requestId: frame.transportRequestId,
          ohtId: frame.ohtId,
          foupId: frame.foupId,
          portId: frame.portId,
          ...((stockerId ?? toolId) ? { stationId: stockerId ?? toolId } : {}),
          ...(typeof frame.x === 'number' && Number.isFinite(frame.x) ? { handoffX: frame.x } : {}),
          ...(typeof frame.y === 'number' && Number.isFinite(frame.y) ? { handoffY: frame.y } : {}),
          delta: frame.eventType === 'PICKUP' ? -1 : 1,
          ...(frame.occupancy != null ? { inventoryAfter: frame.occupancy } : {}),
        };
        if (!this.transferEvents.has(eventId)) {
          const stationId = resolveTransferStationId({
            stockerId,
            toolId,
            portId: frame.portId,
          });
          if (stationId) {
            transferAnimationPipelineStore.enqueue({
              eventId,
              stationId,
              portId: frame.portId,
              action: frame.eventType,
              foupId: frame.foupId,
              ohtId: frame.ohtId,
              stockerId,
              toolId,
            });
          }
          this.transferFrames.set(eventId, {
            frame,
            sequence: this.ingestSequence++,
          });
          this.sequence += 1;
          this.transferEvents.set(eventId, event);
          // Commit ownership at the actual equipment handover edge. A tool's
          // resumeSimTimeS includes processing and is not the OHT release time.
          const ownershipT = visualEndS;
          const completeId = `${eventId}:complete`;
          const { endT: handoffEndT, ...ownershipBase } = event;
          void handoffEndT;
          this.transferEvents.set(completeId, {
            ...ownershipBase,
            sequence: this.sequence,
            eventId: completeId,
            kind: frame.eventType === 'PICKUP' ? 'OHT_PICKUP' : 'OHT_DROP',
            phase: 'complete',
            t: Math.max(frame.simTimeS, ownershipT),
          });
          this.sequence += 1;
          this.transferEventsRevision += 1;
          this.applyTransferProjection(event, {
            ...(stockerId ? { stockerId } : {}),
            column: frame.column,
            columnOccupancy: frame.columnOccupancy,
          });
          if (
            stockerId &&
            frame.column &&
            typeof frame.columnOccupancy === 'number'
          ) {
            this.inventory =
              applyLiveStockerInventoryChange(this.inventory, {
                stockerId,
                column: frame.column,
                columnOccupancy: frame.columnOccupancy,
                occupancy: frame.occupancy,
                change: frame.eventType === 'DROP' ? 'arrived' : 'picked',
              }) ?? undefined;
          }
        } else {
          this.reportDuplicateOnce(`transfer:${eventId}`, () => ({
            type: frame.type,
            eventId,
            ohtId: frame.ohtId,
            simTimeS: frame.simTimeS,
            payload: frame,
          }));
        }
        break;
      }
      case 'transport_request': {
        const id = transportRequestId(frame.transportRequest);
        if (id) this.transportRequests.set(id, frame.transportRequest);
        break;
      }
      case 'transport_request_update': {
        const current = this.transportRequests.get(frame.transportRequestId);
        const assignedOHT = frame.assignedOht ?? frame.ohtId;
        this.transportRequests.set(frame.transportRequestId, {
          ...(current ?? {
            transportRequestId: frame.transportRequestId,
            requestId: frame.transportRequestId,
            pickup: '',
            drop: '',
          }),
          status: frame.status,
          ...(assignedOHT ? { assignedOHT } : {}),
          ...(frame.foupId ? { foupId: frame.foupId } : {}),
        });
        break;
      }
      case 'inventory':
        this.inventory = frame.inventory;
        if (frame.kpis) this.kpis = frame.kpis;
        if (frame.liveReport) this.liveReport = frame.liveReport;
        break;
      case 'collision_wait':
        this.collisions.set(frame.ohtId, frame);
        break;
      case 'collision_clear':
        this.collisions.delete(frame.ohtId);
        break;
      case 'junction_wait': {
        const key = `${frame.ohtId}:${frame.simTimeS}:${frame.nodeId ?? ''}`;
        if (this.junctionWaits.has(key)) {
          this.reportDuplicateOnce(`junction:${key}`, () => ({
            type: frame.type,
            eventId: key,
            ohtId: frame.ohtId,
            simTimeS: frame.simTimeS,
            payload: frame,
          }));
        }
        const normalizedFrame = normalizeJunctionWait(frame);
        this.junctionWaits.set(key, normalizedFrame);
        if (!this.junctionWaitSequences.has(key)) {
          this.junctionWaitSequences.set(key, this.ingestSequence++);
        }
        // A junction_wait is an authoritative stationary movement interval.
        // Materialize it as a high-priority playback leg so both 2D and 3D
        // stop the addressed OHT at the streamed pre-junction coordinates.
        // The leg is available as lookahead, but only becomes active when the
        // render clock reaches simTimeS.
        if (
          normalizedFrame.reason === 'junctionDwell' &&
          typeof normalizedFrame.x === 'number' &&
          Number.isFinite(normalizedFrame.x) &&
          typeof normalizedFrame.y === 'number' &&
          Number.isFinite(normalizedFrame.y)
        ) {
          const endS =
            typeof normalizedFrame.untilSimTimeS === 'number' &&
              Number.isFinite(normalizedFrame.untilSimTimeS) &&
              normalizedFrame.untilSimTimeS >= normalizedFrame.simTimeS
              ? normalizedFrame.untilSimTimeS
              : normalizedFrame.simTimeS +
              Math.max(0, asNumber(normalizedFrame.waitSec ?? normalizedFrame.holdSec));
          const legId = `junction-wait:${normalizedFrame.ohtId}:${normalizedFrame.nodeId ?? ''}:${normalizedFrame.simTimeS}`;
          this.legs.set(legId, {
            legId,
            vehicleId: normalizedFrame.ohtId,
            ohtId: normalizedFrame.ohtId,
            requestId: normalizedFrame.requestId ?? '',
            foupId: normalizedFrame.foupId ?? undefined,
            startS: normalizedFrame.simTimeS,
            endS,
            fromId: normalizedFrame.nodeId ?? '',
            toId: normalizedFrame.nodeId ?? '',
            fromX: normalizedFrame.x,
            fromY: normalizedFrame.y,
            toX: normalizedFrame.x,
            toY: normalizedFrame.y,
            phase: 'wait',
            speed: 0,
            waitS: Math.max(0, asNumber(normalizedFrame.waitSec ?? normalizedFrame.holdSec)),
            reason: normalizedFrame.reason || 'junctionDwell',
          });
        }
        break;
      }
      case 'stream_layout': {
        const layout = frame as unknown as Record<string, unknown>;
        this.inventory =
          mapStreamInventory(
            this.inventory,
            layout.stockerInventory as AmhsLiveInventory['stockers']
          ) ?? this.inventory;
        break;
      }
      case 'stocker_inventory': {
        const change = frame as unknown as StockerInventoryChange;
        this.inventory =
          applyLiveStockerInventoryChange(this.inventory, change) ?? undefined;
        break;
      }
      default:
        if (
          ![
            'stream_layout',
            'stocker_inventory',
            'bay_inventory',
            'heartbeat',
            'stream_stopped',
            'stream_closed',
            'error',
          ].includes((frame as { type: string }).type)
        ) {
          if (this.withinLogBudget('UNKNOWN_EVENT')) {
            console.warn('[UNKNOWN_EVENT]', {
              type: (frame as { type: string }).type,
              simTimeS: frame.simTimeS,
              payload: frame,
            });
          }
        }
        break;
    }
    this.pruneRetainedHistory();
  }

  drain(): AmhsLiveReplicaBatch {
    const activeWaits: AmhsLiveReplicaBatch['collisions'] = Object.fromEntries(this.collisions);
    for (const wait of this.junctionWaits.values()) {
      if (wait.untilSimTimeS <= this.simTimeS) continue;
      if (wait.simTimeS <= this.simTimeS) activeWaits[wait.ohtId] = wait;
    }
    const junctionCorrelation = this.correlateJunctionWaits();
    const orderedLegs = this.orderedContinuousLegs(junctionCorrelation.duplicateSyntheticLegIds);
    return {
      simTimeS: this.simTimeS,
      legs: orderedLegs,
      transferEvents: this.orderedTransferEvents(),
      transportRequests: [...this.transportRequests.values()],
      ...(this.inventory ? { inventory: this.inventory } : {}),
      ...(this.kpis ? { kpis: this.kpis } : {}),
      ...(this.liveReport ? { liveReport: this.liveReport } : {}),
      collisions: activeWaits,
      ohtCargo: Object.fromEntries(this.ohtCargo),
      lastStockerDrops: Object.fromEntries(this.lastStockerDrops),
      toolPickupReady: collectToolPickupReady(
        [...this.transferFrames.values()].map((entry) => entry.frame)
      ),
      ohtTimelines: this.buildOhtTimelines(orderedLegs, junctionCorrelation.correlatedLegByWaitKey),
      ...(this.eventCursor != null ? { eventCursor: this.eventCursor } : {}),
    };
  }

  /**
   * Transfer events ordered for replay, reusing the previous array while the
   * log is unchanged.
   *
   * Arrival order and simulation order diverge, so sequences are re-indexed
   * after sorting: `buildReplayPlan` and its checkpoint search require
   * sequences ascending with `t`, otherwise a seek restores the wrong
   * ownership ledger. Returning a stable array reference also lets the reducer
   * keep the previous replay plan, which is what most flushes need.
   */
  private orderedTransferEvents(): VisualizationEvent[] {
    const cached = this.drainedTransferEvents;
    if (cached && cached.revision === this.transferEventsRevision) return cached.events;
    const events = [...this.transferEvents.values()]
      .sort((a, b) => a.t - b.t || a.sequence - b.sequence)
      .map((event, index) => (event.sequence === index ? event : { ...event, sequence: index }));
    this.drainedTransferEvents = { revision: this.transferEventsRevision, events };
    return events;
  }

  private correlateJunctionWaits(): {
    correlatedLegByWaitKey: Map<string, string>;
    duplicateSyntheticLegIds: Set<string>;
  } {
    const correlatedLegByWaitKey = new Map<string, string>();
    const duplicateSyntheticLegIds = new Set<string>();
    const toleranceS = 0.01;
    const legsByVehicle = new Map<string, AmhsStreamVehicleLeg[]>();
    for (const leg of this.legs.values()) {
      const legs = legsByVehicle.get(leg.vehicleId);
      if (legs) legs.push(leg);
      else legsByVehicle.set(leg.vehicleId, [leg]);
    }

    // Index once per drain. Scanning every retained leg for each wait made this
    // O(waits x legs) and re-spread the whole leg map on every flush.
    const dwellLegsByOht = new Map<string, AmhsStreamVehicleLeg[]>();
    for (const leg of this.legs.values()) {
      if (leg.legId.startsWith('junction-wait:')) continue;
      if (leg.reason !== 'junctionDwell') continue;
      const list = dwellLegsByOht.get(leg.vehicleId);
      if (list) list.push(leg);
      else dwellLegsByOht.set(leg.vehicleId, [leg]);
    }

    for (const [key, wait] of this.junctionWaits) {
      if (wait.reason !== 'junctionDwell') continue;
      const waitEnd = wait.untilSimTimeS;
      const match = legsByVehicle.get(wait.ohtId)?.find(
        (leg) =>
          !leg.legId.startsWith('junction-wait:') &&
          leg.reason === 'junctionDwell' &&
          leg.fromId === leg.toId &&
          (leg.speed === 0 || (leg.fromX === leg.toX && leg.fromY === leg.toY)) &&
          (!wait.nodeId || leg.fromId === wait.nodeId) &&
          (!wait.requestId || !leg.requestId || leg.requestId === wait.requestId) &&
          Math.abs(leg.startS - wait.simTimeS) <= toleranceS &&
          Math.abs(leg.endS - waitEnd) <= toleranceS
      );
      if (!match) continue;
      correlatedLegByWaitKey.set(key, match.legId);
      duplicateSyntheticLegIds.add(
        `junction-wait:${wait.ohtId}:${wait.nodeId ?? ''}:${wait.simTimeS}`
      );
    }
    return { correlatedLegByWaitKey, duplicateSyntheticLegIds };
  }

  private buildOhtTimelines(
    orderedLegs: AmhsStreamVehicleLeg[],
    correlatedLegByWaitKey: Map<string, string>
  ): Record<string, OhtTimelineEvent[]> {
    const timelines: Record<string, OhtTimelineEvent[]> = {};
    const push = (ohtId: string, event: OhtTimelineEvent) => {
      (timelines[ohtId] ??= []).push(event);
    };

    for (const leg of orderedLegs) {
      push(leg.vehicleId, {
        type: 'vehicle_leg',
        eventId: leg.legId,
        ohtId: leg.vehicleId,
        requestId: leg.requestId,
        startS: leg.startS,
        endS: leg.endS,
        sequence: this.legSequences.get(leg.legId) ?? 0,
        streamSequence: leg.streamSequence,
        stationary: leg.speed === 0 || (leg.fromX === leg.toX && leg.fromY === leg.toY),
        leg,
      });
    }

    for (const [eventId, entry] of this.transferFrames) {
      const { frame, sequence } = entry;
      const holdSimSec =
        typeof frame.holdSimSec === 'number'
          ? Math.max(0, frame.holdSimSec)
          : typeof frame.resumeSimTimeS === 'number'
            ? Math.max(0, frame.resumeSimTimeS - frame.simTimeS)
            : 0;
      const resumeSimTimeS =
        typeof frame.resumeSimTimeS === 'number'
          ? frame.resumeSimTimeS
          : frame.simTimeS + holdSimSec;
      const equipmentVehicleFreeS =
        optionalId(frame.toolId) &&
          frame.eventType === 'DROP' &&
          typeof frame.vehicleFreeSimTimeS === 'number' &&
          Number.isFinite(frame.vehicleFreeSimTimeS) &&
          frame.vehicleFreeSimTimeS >= frame.simTimeS
          ? frame.vehicleFreeSimTimeS
          : undefined;
      const transferEndS = equipmentVehicleFreeS ?? resumeSimTimeS;
      const transferHoldSimSec = Math.max(0, transferEndS - frame.simTimeS);
      // Convert the vehicle's share of the simulation hold to the corresponding
      // wall duration published for visualization.
      const transferHoldWallSec =
        equipmentVehicleFreeS !== undefined && holdSimSec > 0
          ? Math.max(0, frame.holdWallSec ?? 0) *
          (transferHoldSimSec / holdSimSec)
          : Math.max(0, frame.holdWallSec ?? 0);
      push(frame.ohtId, {
        type: 'transfer_event',
        eventId,
        ohtId: frame.ohtId,
        streamId: frame.streamId,
        requestId: frame.transportRequestId,
        startS: frame.simTimeS,
        endS: transferEndS,
        sequence,
        streamSequence: frame.sequence,
        action: frame.eventType,
        holdWallSec: transferHoldWallSec,
        holdSimSec: transferHoldSimSec,
        resumeSimTimeS: transferEndS,
        frame,
      });
    }

    for (const [key, frame] of this.junctionWaits) {
      push(frame.ohtId, {
        type: 'junction_wait',
        eventId: key,
        ohtId: frame.ohtId,
        streamId: frame.streamId,
        requestId: frame.requestId,
        startS: frame.simTimeS,
        endS: frame.untilSimTimeS,
        sequence: this.junctionWaitSequences.get(key) ?? 0,
        streamSequence: frame.sequence,
        reason: frame.reason,
        nodeId: frame.nodeId,
        blockingOhtId: optionalId(frame.blockingOhtId),
        correlatedLegId: correlatedLegByWaitKey.get(key),
        frame,
      });
    }

    const typePriority: Record<OhtTimelineEvent['type'], number> = {
      vehicle_leg: 0,
      junction_wait: 1,
      transfer_event: 2,
    };
    for (const events of Object.values(timelines)) {
      events.sort(
        (a, b) =>
          a.startS - b.startS ||
          (a.streamSequence != null && b.streamSequence != null
            ? a.streamSequence - b.streamSequence
            : 0) ||
          typePriority[a.type] - typePriority[b.type] ||
          a.eventId.localeCompare(b.eventId) ||
          a.sequence - b.sequence
      );
      for (let index = 1; index < events.length; index += 1) {
        const previous = events[index - 1]!;
        const current = events[index]!;
        if (
          // The dev gate comes first so a release build — and every tick of a
          // long dev run past the budget — skips building the key string for
          // each equal-timestamped pair of every OHT's retained timeline.
          import.meta.env.DEV &&
          previous.startS === current.startS &&
          previous.streamSequence == null &&
          current.streamSequence == null
        ) {
          const key = `${previous.ohtId}:${previous.startS}:${previous.eventId}:${current.eventId}`;
          if (!this.reportedOrderAmbiguities.has(key)) {
            if (this.reportedOrderAmbiguities.size >= 5000) {
              this.reportedOrderAmbiguities.clear();
            }
            this.reportedOrderAmbiguities.add(key);
            if (this.withinLogBudget('EVENT_ORDER_AMBIGUOUS')) {
              console.warn('[EVENT_ORDER_AMBIGUOUS]', {
                ohtId: previous.ohtId,
                simulationTimeS: previous.startS,
                eventIds: [previous.eventId, current.eventId],
                action: 'deterministic_type_and_id_tiebreak',
              });
            }
          }
        }
      }
    }
    return timelines;
  }

  private applyTransferProjection(
    event: VisualizationEvent,
    details?: { stockerId?: string; column?: string; columnOccupancy?: number }
  ): void {
    const stockerId = details?.stockerId ?? event.stationId;
    if (event.action !== 'DROP' || !stockerId || !event.foupId || !event.ohtId || !event.portId) {
      return;
    }
    const knownStocker = this.inventory?.stockers?.some(
      (stocker) => stocker.stockerId === stockerId
    );
    if (!details?.stockerId && !knownStocker) return;
    const previous = this.lastStockerDrops.get(stockerId);
    if (previous && previous.simTimeS > event.t) return;
    this.lastStockerDrops.set(stockerId, {
      stockerId,
      foupId: event.foupId,
      ohtId: event.ohtId,
      portId: event.portId,
      simTimeS: event.t,
      ...(details?.column ? { column: details.column } : {}),
      ...(details?.columnOccupancy != null ? { columnOccupancy: details.columnOccupancy } : {}),
    });
  }

  private pruneRetainedHistory(): void {
    for (const [key, wait] of this.junctionWaits) {
      if (wait.untilSimTimeS <= this.simTimeS - 1) {
        this.junctionWaits.delete(key);
        this.junctionWaitSequences.delete(key);
      }
    }

    if (this.transferFrames.size > this.maxRetainedTransferFrames) {
      const sorted = [...this.transferFrames.entries()].sort(
        (a, b) => a[1].frame.simTimeS - b[1].frame.simTimeS
      );
      const excess = sorted.length - this.maxRetainedTransferFrames;
      for (let index = 0; index < excess; index += 1) {
        this.transferFrames.delete(sorted[index]![0]);
      }
    }

    if (this.transferEvents.size > this.maxRetainedTransferEvents) {
      const sorted = [...this.transferEvents.entries()].sort(
        (a, b) => a[1].t - b[1].t || a[1].sequence - b[1].sequence
      );
      const excess = sorted.length - this.maxRetainedTransferEvents;
      for (let index = 0; index < excess; index += 1) {
        this.transferEvents.delete(sorted[index]![0]);
      }
      this.transferEventsRevision += 1;
      this.drainedTransferEvents = null;
    }

    if (this.transportRequests.size > this.maxRetainedTransportRequests) {
      const entries = [...this.transportRequests.entries()];
      const excess = entries.length - this.maxRetainedTransportRequests;
      for (let index = 0; index < excess; index += 1) {
        this.transportRequests.delete(entries[index]![0]);
      }
    }

    this.pruneLegs();
  }

  private pruneLegs(): void {
    if (this.legs.size <= this.maxRetainedLegs) return;

    // Keep the current/latest executed leg for every OHT as its continuity
    // anchor. Without this, global pruning can leave only a future look-ahead
    // leg for one vehicle. Redux then derives that vehicle's idle point from
    // the future leg and it teleports there before startS.
    const latestStartedByVehicle = new Map<string, AmhsStreamVehicleLeg>();
    for (const leg of this.legs.values()) {
      const executionTime = this.executionTimeByOht.get(leg.vehicleId) ?? this.simTimeS;
      if (leg.startS > executionTime) continue;
      const current = latestStartedByVehicle.get(leg.vehicleId);
      if (
        !current ||
        leg.startS > current.startS ||
        (leg.startS === current.startS && leg.endS > current.endS)
      ) {
        latestStartedByVehicle.set(leg.vehicleId, leg);
      }
    }
    const protectedLegIds = new Set([...latestStartedByVehicle.values()].map((leg) => leg.legId));
    const sorted = [...this.legs.values()].sort((a, b) => a.endS - b.endS || a.startS - b.startS);
    for (const leg of sorted) {
      if (this.legs.size <= this.maxRetainedLegs) break;
      if (protectedLegIds.has(leg.legId)) continue;
      // Look-ahead legs are the pending execution queue, not retention
      // history. Compare against the shifted OHT clock: global stream time can
      // be hundreds of seconds ahead after a wall-authoritative transfer.
      const executionTime = this.executionTimeByOht.get(leg.vehicleId) ?? this.simTimeS;
      if (leg.startS > executionTime) continue;
      this.legs.delete(leg.legId);
      this.legSequences.delete(leg.legId);
    }
    // The map may temporarily exceed the cap by continuity anchors plus the
    // bounded server look-ahead window. Correct scheduling is more important
    // than deleting an OHT's only location history or pending route.
  }

  /**
   * Build independent, time-ordered OHT queues and withhold discontinuous route
   * branches until their missing bridge arrives. A future out-of-order leg is
   * still stored in `legs`; it simply cannot move the vehicle meanwhile.
   */
  private orderedContinuousLegs(
    excludedLegIds: ReadonlySet<string> = new Set()
  ): AmhsStreamVehicleLeg[] {
    const byVehicle = new Map<string, AmhsStreamVehicleLeg[]>();
    for (const leg of this.legs.values()) {
      if (excludedLegIds.has(leg.legId)) continue;
      const queue = byVehicle.get(leg.vehicleId) ?? [];
      queue.push(leg);
      byVehicle.set(leg.vehicleId, queue);
    }

    const result: AmhsStreamVehicleLeg[] = [];
    for (const [vehicleId, queue] of byVehicle) {
      queue.sort(
        (a, b) => a.startS - b.startS || a.endS - b.endS || a.legId.localeCompare(b.legId)
      );
      const accepted: AmhsStreamVehicleLeg[] = [];
      for (const leg of queue) {
        let previous: AmhsStreamVehicleLeg | undefined;
        for (let index = accepted.length - 1; index >= 0; index -= 1) {
          const candidate = accepted[index];
          if (candidate.endS <= leg.startS + 1e-6) {
            previous = candidate;
            break;
          }
        }

        // A streamed junction dwell is an authoritative stationary interval at
        // the hub. Its adjacent travel chords may end/start on the spoke rather
        // than at the exact hub coordinate, so it chains by time order. Keep
        // strict node/coordinate continuity for real travel-to-travel legs.
        const involvesJunctionWait =
          previous !== undefined &&
          (isJunctionWaitLeg(previous) || isJunctionWaitLeg(leg));
        const routeMismatch =
          previous !== undefined &&
          !involvesJunctionWait &&
          previous.toId !== leg.fromId &&
          Math.hypot(previous.toX - leg.fromX, previous.toY - leg.fromY) > 1e-4;
        if (routeMismatch) {
          const mismatchKey = `${vehicleId}:${previous!.legId}:${leg.legId}`;
          if (!this.reportedRouteMismatches.has(mismatchKey)) {
            // Leg ids keep turning over as legs are pruned and re-added, so
            // cap the identity set alongside the report budget.
            if (this.reportedRouteMismatches.size >= 5000) {
              this.reportedRouteMismatches.clear();
            }
            this.reportedRouteMismatches.add(mismatchKey);
            if (this.withinLogBudget('ROUTE_MISMATCH')) {
              console.warn('[ROUTE_MISMATCH]', {
                ohtId: vehicleId,
                previousLegId: previous!.legId,
                nextLegId: leg.legId,
                previousTo: previous!.toId,
                nextFrom: leg.fromId,
                action: 'held_last_physical_position',
                previous,
                current: leg,
              });
            }
          }
          continue;
        }
        accepted.push(leg);
      }
      result.push(...accepted);
    }
    return result.sort(
      (a, b) =>
        a.startS - b.startS ||
        a.endS - b.endS ||
        a.vehicleId.localeCompare(b.vehicleId) ||
        a.legId.localeCompare(b.legId)
    );
  }
}
